import { useEffect, useRef } from "react";
import type { LogEntry } from "../lib/types";

interface LogStreamProps {
  entries: LogEntry[];
  isPolling: boolean;
  lastError: string | null;
}

export function LogStream({ entries, isPolling, lastError }: LogStreamProps) {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Auto-scroll to the bottom when new entries arrive.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [entries.length]);

  if (entries.length === 0 && !isPolling && !lastError) return null;

  return (
    <div className="log-stream-wrap">
      <div className="log-stream-header">
        <span>Logs</span>
        {isPolling && <span className="log-stream-status">⏳ tailing…</span>}
        {lastError && (
          <span className="log-stream-status log-stream-error">
            polling error: {lastError}
          </span>
        )}
      </div>
      <div className="log-stream" ref={scrollRef}>
        {entries.length === 0 ? (
          <div className="log-line log-empty">(waiting for entries…)</div>
        ) : (
          entries.map((e, i) => (
            <div
              key={`${e.timestamp ?? "x"}-${i}`}
              className={`log-line log-${e.severity.toLowerCase()}`}
            >
              <span className="log-ts">{formatTimeOfDay(e.timestamp)}</span>
              <span className="log-sev">{e.severity}</span>
              <span className="log-text">{e.text}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function formatTimeOfDay(iso: string | null): string {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString();
  } catch {
    return iso;
  }
}
